import { useCallback, useEffect, useState } from "react";
import { API_URL, useAuth } from "./AuthContext";

// The trader's profit payout requests (GET /api/trading-account/withdrawals), newest
// first. `request` posts a new one and resolves to { withdrawal } or { error }, so the
// payout card and modal can show the message inline. The API works out the figures.
// Off while `enabled` is false (the state-switcher previews), when there's nothing to load.
export default function useWithdrawals(enabled) {
  const { token } = useAuth();
  const [withdrawals, setWithdrawals] = useState(null);

  useEffect(() => {
    if (!enabled) return undefined;
    const controller = new AbortController();
    fetch(`${API_URL}/api/trading-account/withdrawals`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
      .then((body) => setWithdrawals(body.withdrawals))
      .catch((err) => {
        if (err.name === "AbortError") return;
        console.error("Failed to load payout requests:", err);
        setWithdrawals([]);
      });
    return () => controller.abort();
  }, [enabled, token]);

  const request = useCallback(
    async ({ kind, amount, address }) => {
      try {
        const res = await fetch(`${API_URL}/api/trading-account/withdrawals`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({ kind, amount, address }),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) return { error: json.error || "Something went wrong. Please try again." };
        setWithdrawals((prev) => [json.withdrawal, ...(prev || [])]);
        return json;
      } catch {
        return { error: "Couldn't reach the server. Check your connection and try again." };
      }
    },
    [token]
  );

  return { withdrawals, request };
}
